import React, { useEffect, useId } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDrawer, LntForm, LntFormItem, LntInput, LntSwitch } from '@app/components/base'

import { PRICING_KEY } from '@app/constants/queryKeys'
import { SIZE, dateFormat } from '@app/constants'

import { getPricingConfig, patchPricingConfig } from '@app/api/routes/pricing'

import { useNotification } from '@app/hooks'
import { getFormattedDate } from '@app/utils/date'

import { adaptPricingConfigFormData } from './NotificationsTimes/lib'
import { NotificationsTimes } from './NotificationsTimes'

import styles from './styles.module.scss'

import type { PricingConfigData } from '@app/api/routes/pricing/types'
import type { TransferSettingsDrawerProps, TransferSettingsFormData } from './types'

const title = 'Настройки передачи'

export const TransferSettingsDrawer: React.FC<TransferSettingsDrawerProps> = ({ onClose, onSave, ...drawerProps }) => {
  const formId = useId()

  const queryClient = useQueryClient()
  const notification = useNotification()
  const form = useForm<TransferSettingsFormData>()

  const { data: configData } = useQuery({
    queryKey: [PRICING_KEY.pricingConfig],
    queryFn: async () => await getPricingConfig()
  })

  const resetFormDataByConfig = (): void => {
    form.reset(adaptPricingConfigFormData(configData))
  }

  const handleClose = (): void => {
    resetFormDataByConfig()
    onClose()
  }

  const patchConfigDataMutation = useMutation({
    mutationFn: patchPricingConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [PRICING_KEY.pricingConfig] })

      notification.success({
        message: 'Успешно сохранено.'
      })

      onSave?.()
    }
  })

  const handleSubmit = (formData: TransferSettingsFormData): void => {
    const { notificationsTimes, ...restFormData } = formData

    const formattedTimes = notificationsTimes.reduce<string[]>((acc, time) => {
      const formattedTime = getFormattedDate(time, dateFormat.HH_mm_ss)

      if (formattedTime !== null) {
        acc.push(formattedTime)
      }

      return acc
    }, [])

    const transformedValues: PricingConfigData = {
      ...restFormData,
      notificationsTimes: formattedTimes
    }

    patchConfigDataMutation.mutate(transformedValues)
  }

  useEffect(() => {
    resetFormDataByConfig()
  }, [configData])

  const footerButtons: React.ReactNode = (
    <div className={ styles.transferSettingsDrawer__footer }>
      <LntButton
        htmlType='submit'
        form={ formId }
        disabled={ !form.formState.isValid || !form.formState.isDirty }
      >
        Сохранить
      </LntButton>
    </div>
  )

  return (
    <LntDrawer
      { ...drawerProps }
      title={ title }
      width={ 424 }
      footer={ footerButtons }
      onClose={ handleClose }
    >
      <LntForm<TransferSettingsFormData>
        layout='inline'
        onSubmit={ handleSubmit }
        formInstance={ form }
        id={ formId }
      >
        <div className={ styles.transferSettingsDrawer__items }>
          <LntFormItem
            name='exportAbnormalResults'
            label='Передача аномальных результатов'
            rootClassName='test'
          >
            <LntSwitch
              size={ SIZE.small }
              checked={ configData?.exportAbnormalResults }
            />
          </LntFormItem>
          <LntFormItem
            name='outdatedThresholdInDays'
            label='Период актуальности результата'
            className={ styles.transferSettingsDrawer__horizontalItem }
          >
            <LntInput
              className={ styles.transferSettingsDrawer__input }
              placeholder='Кол-во дней'
              type='number'
            />
          </LntFormItem>

          <NotificationsTimes />
        </div>
      </LntForm>
    </LntDrawer>
  )
}
